import { getTaskLabelForTask } from '@seen/core/models/taskLabel/getTaskLabelFromTask'
import { QueuedEditsReviewTask } from '@seen/core/models/tasks/standard/queuedEditsReviewTask'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'

const promoteGeneration = { current: 0 }

// Opens the shared task composer prefilled to file an inline (Task-less)
// queued-edits proposal Bundle as a QueuedEditsReviewTask — so the review can be
// found and acted on outside the chat. The composer doubles as the confirmation
// step and lets the user reassign the owner before creating. The Bundle stays
// the source of truth; the composer creates a Task carrying the queued-edits
// review code, and `onTaskCreated` attaches the Bundle on Task.focus so the
// inbox renders it.
export function usePromoteBundleToTaskHandler(): (
  bundleReference: string,
  onPromoted?: (taskReference: string) => void
) => void {
  const medplum = useMedplum()
  const patientId = useAIAssistantStore((state) => state.patientId)
  const setOpen = useTaskComposerStore((state) => state.setOpen)
  const setDefaultValues = useTaskComposerStore(
    (state) => state.setDefaultValues
  )
  const setOnTaskCreated = useTaskComposerStore(
    (state) => state.setOnTaskCreated
  )

  return useCallback(
    (bundleReference: string, onPromoted?: (taskReference: string) => void) => {
      const generation = ++promoteGeneration.current
      const initialComposerState = useTaskComposerStore.getState()
      const isCurrent = (): boolean => {
        const composerState = useTaskComposerStore.getState()
        return (
          generation === promoteGeneration.current &&
          composerState.open === initialComposerState.open &&
          composerState.defaultValues === initialComposerState.defaultValues &&
          composerState.onTaskCreated === initialComposerState.onTaskCreated
        )
      }
      const participant = patientId
        ? ({ reference: `Patient/${patientId}` } as const)
        : undefined
      const draft = new StandardTask({ code: QueuedEditsReviewTask.code })

      void getTaskLabelForTask(medplum, draft)
        .then((label) => {
          if (!isCurrent()) {
            return
          }
          setDefaultValues({
            title: 'Tag change',
            description:
              'The Seen AI assistant proposed a change to this participant’s tags. Review and apply or reject it.',
            participant,
            code: QueuedEditsReviewTask.code,
            labels: [label.reference],
          })
          // After the composer creates the task, attach the proposal Bundle so
          // the task's review card knows what to render. The composer creates a
          // plain StandardTask; re-wrap it to set the typed proposed bundle.
          setOnTaskCreated((task) => {
            const review = QueuedEditsReviewTask.fromFhirResource(
              task.toFhirResource()
            )
            // onTaskCreated is synchronous, so the patch is fire-and-forget.
            // Only navigate to the task after the Bundle attachment succeeds.
            medplum
              .patchResourceInstance(review, (t) => {
                t.proposedBundle = { reference: bundleReference }
              })
              .then(() => {
                onPromoted?.(review.reference.reference)
              })
              .catch(() => {
                toast.error(
                  'Saved the task but could not attach the proposed tag change. Please retry from the chat.'
                )
              })
          })
          setOpen(true)
        })
        .catch(() => {
          if (isCurrent()) {
            toast.error('Could not prepare the queued edit task. Please retry.')
          }
        })
    },
    [medplum, patientId, setOpen, setDefaultValues, setOnTaskCreated]
  )
}
